import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../scripts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastError, toastSuccess } from "@/lib/toast";

const selectClassName =
  "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

/**
 * Create a standalone social community: /communities/new
 */
const CreateCommunityPage = () => {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [joinPolicy, setJoinPolicy] = useState("open");
  const [postingPolicy, setPostingPolicy] = useState("community_posts");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const name = displayName.trim();
    if (name.length < 2) {
      setError("Name must be at least 2 characters");
      return;
    }
    if (!accessToken) return;
    setBusy(true);
    try {
      const body = {
        display_name: name,
        join_policy: joinPolicy,
        posting_policy: postingPolicy,
      };
      const desc = description.trim();
      if (desc) body.description = desc;

      const { data } = await axios.post(
        `${API_BASE_URL}/social/communities`,
        body,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const id = data?.community_id;
      toastSuccess("Community created");
      if (id != null) {
        navigate(`/c/${id}`);
      } else {
        navigate("/explore");
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Could not create community";
      setError(Array.isArray(msg) ? msg.join(", ") : msg);
      toastError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg px-4 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Create community
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Start a community for people to join and post.
        </p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="community-name">Name</Label>
          <Input
            id="community-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Community name"
            maxLength={200}
            disabled={busy}
            autoFocus
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="community-description">Description</Label>
          <textarea
            id="community-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this community about? (optional)"
            maxLength={5000}
            disabled={busy}
            rows={4}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="community-join">Who can join</Label>
          <select
            id="community-join"
            value={joinPolicy}
            onChange={(e) => setJoinPolicy(e.target.value)}
            className={selectClassName}
            disabled={busy}
          >
            <option value="open">Anyone</option>
            <option value="request">Request to join</option>
            <option value="invite_only">Invite only</option>
          </select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="community-posting">Who can post</Label>
          <select
            id="community-posting"
            value={postingPolicy}
            onChange={(e) => setPostingPolicy(e.target.value)}
            className={selectClassName}
            disabled={busy}
          >
            <option value="community_posts">Members can author posts</option>
            <option value="official_only">
              Restricted (you can change this later)
            </option>
          </select>
          <p className="text-xs text-muted-foreground">
            You can own up to 5 communities. Policies can be changed later.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" asChild disabled={busy}>
            <Link to="/explore">Cancel</Link>
          </Button>
          <Button type="submit" disabled={busy || displayName.trim().length < 2}>
            {busy ? "Creating…" : "Create community"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateCommunityPage;
